// Centralised site content — bilingual where needed
import {
  Droplets, Wrench, ShieldCheck, FlaskConical, Cpu, Settings, ClipboardCheck,
  Lightbulb, Users, Cog, PackageSearch, FileSearch, Gauge, MapPin
} from "lucide-react";

export const COMPANY = {
  name: "Waves Technology FZCO",
  legalShort: "Waves Technology FZC.",
  tagline: "Your Partner for Engineered Systems",
  email: "info@waves-technology.net",
  website: "www.waves-technology.net",
  websiteHref: "https://www.waves-technology.net",
  address: "Dubai Silicon Oasis, DDP, Building A1, Dubai, UAE",
  phone: "+971 58 505 5317",
  phoneHref: "tel:+971585055317",
  whatsapp: "971585055317",
  whatsappMessage: "Hello Waves Technology, I would like to discuss a project / request a quotation.",
  logos: {
    transparent: "/assets/logo.png",
    noBuffer: "/assets/logo.png",
  },
};

// Images — water treatment, process plants, engineering and industrial infrastructure only.
// No offshore platforms, no drilling rigs, no marine oil & gas imagery.
// Real product photos provided by Waves Technology where available.
export const IMG = {
  heroBg: "/assets/hero.jpg",
  waterTreatment: "/assets/ro.png",
  roMembranes: "/assets/ro.png",
  cartridgeFilters: "/assets/cartridge.png",
  controlRoom: "/assets/control.jpg",
  engineeringReview: "/assets/engineering.jpg",
  powerPlant: "/assets/petrochem.jpg",
  // Aliases for backward compatibility
  waterPlant: "/assets/ro.png",
  processPlant: "/assets/ro.png",
  pipes: "/assets/ro.png",
  engineers: "/assets/engineering.jpg",
  petrochem: "/assets/petrochem.jpg",
  industrialFacility: "/assets/cartridge.png",
};

// Local strategic partners (only these two for now)
export const LOCAL_PARTNERS = [
  {
    key: "etmam",
    name: "Etmam for General Contracting",
    role: "Local Partner — Iraq",
    region: "Iraq",
    description: "Local contracting and project execution partner supporting industrial and infrastructure projects in Iraq.",
    logo: "/assets/logo.png",
    website: "https://etmam-co.com/",
    websiteLabel: "etmam-co.com",
  },
  {
    key: "uniserve",
    name: "Uniserve",
    role: "Local Partner — Egypt & Libya",
    region: "Egypt · Libya",
    description: "Regional business development and project support partner serving Egypt and Libya.",
    logo: "/assets/logo.png",
    email: "uniserve@uniserve-eg.com",
  },
];

// Core capabilities — consolidated cards (Vendor & Technology Coordination and Technical Advisory removed)
export const CORE_SERVICES = [
  { icon: Droplets, key: "water", title: "Water Treatment Solutions",
    desc: "Engineering and procurement support for RO, UF, DAF, clarifier, multimedia filtration, dosing and containerized water treatment systems." },
  { icon: Settings, key: "engineering", title: "Engineering Support Services",
    desc: "Process, mechanical, electrical and instrumentation engineering — calculations, PFDs, P&IDs, datasheets and layouts." },
  { icon: PackageSearch, key: "procurement", title: "Procurement & Sourcing",
    desc: "International sourcing, vendor identification, supplier qualification, expediting and logistics coordination." },
  { icon: Lightbulb, key: "consulting", title: "Project Management & Consulting",
    desc: "Concept development, feasibility, technology selection and budgetary estimation for industrial and infrastructure projects." },
  { icon: FileSearch, key: "tbe", title: "Bidding Support Service",
    desc: "Independent technical and commercial evaluation of vendor offers, document review and clarifications." },
  { icon: ClipboardCheck, key: "commissioning", title: "Inspection & Commissioning Support",
    desc: "FAT / SAT support, pre-commissioning and commissioning assistance through project-specific arrangements." },
];

export const INDUSTRIES = [
  { key: "water", title: "Water & Wastewater",
    desc: "Engineering and integration support for industrial, municipal and wastewater treatment plants.",
    img: "/assets/ro.png" },
  { key: "oilgas", title: "Oil & Gas",
    desc: "Engineering, procurement and technology support for onshore oil & gas process and utility systems, pipelines, pumping stations and tank farms.",
    img: "/assets/hero.jpg" },
  { key: "petro", title: "Petrochemical",
    desc: "Engineering and procurement support for process and utility systems in onshore petrochemical facilities.",
    img: "/assets/petrochem.jpg" },
  { key: "industrial", title: "Industrial Facilities",
    desc: "Engineering, procurement and vendor coordination for cement, manufacturing and utility upgrade projects.",
    img: "/assets/control.jpg" },
  { key: "infra", title: "Infrastructure Projects",
    desc: "Engineering and procurement support for utility infrastructure, pumping stations, water infrastructure and brownfield modifications.",
    img: "/assets/ro.png" },
];

export const LIFECYCLE_STEPS = [
  { n: "01", title: "Concept Development", desc: "Define project objectives, scope boundaries and high-level technical concept." },
  { n: "02", title: "Technical Evaluation", desc: "Technology benchmarking, process selection and feasibility analysis." },
  { n: "03", title: "Engineering Support", desc: "Process, mechanical, electrical and I&C engineering support." },
  { n: "04", title: "Procurement Coordination", desc: "Vendor identification, technical bid evaluation, expediting and inspection coordination." },
  { n: "05", title: "Execution Support", desc: "Construction and installation support through project-specific arrangements." },
  { n: "06", title: "Commissioning Assistance", desc: "FAT / SAT, pre-commissioning and commissioning support." },
];

export const WHY_CHOOSE = [
  { title: "UAE Registered Company", desc: "Registered under IFZA, Dubai — operating with a clear regional and international remit." },
  { title: "Regional Market Knowledge", desc: "Working understanding of Iraq, UAE and Egypt project environments and procurement realities." },
  { title: "Engineering & Procurement Experience", desc: "Hands-on experience across engineering deliverables and procurement support." },
  { title: "International Supplier Network", desc: "Access to a curated network of qualified manufacturers and technology providers." },
  { title: "Technical Evaluation Capability", desc: "Structured technical bid evaluation, datasheet and document review competence." },
  { title: "Water Treatment Specialization", desc: "Focused expertise in industrial, municipal and wastewater treatment technologies." },
  { title: "Vendor Coordination Expertise", desc: "Bridging buyers and suppliers on commercial, technical and logistical fronts." },
  { title: "Flexible Project Support Model", desc: "Engagement scaled to project need — from advisory through to project-specific execution support." },
];

// Services page — consolidated structure
export const SERVICE_CATEGORIES = [
  {
    key: "water-treatment",
    title: "Water Treatment Technology Solutions",
    icon: Droplets,
    image: IMG.waterTreatment,
    items: [
      "Reverse Osmosis (RO) Systems",
      "Ultrafiltration (UF) Systems",
      "DAF Systems",
      "Clarifiers",
      "Multimedia Filtration",
      "Chemical Dosing Systems",
      "Cartridge & Disc Filters",
      "Containerized Water Treatment Plants",
      "Industrial Water Treatment",
      "Municipal Water Treatment",
      "Wastewater Treatment",
    ],
  },
  {
    key: "engineering",
    title: "Engineering Support Services",
    icon: Settings,
    image: IMG.engineeringReview,
    items: [
      "Process Engineering",
      "Process Calculations",
      "PFD Development",
      "P&ID Development",
      "Mechanical Engineering",
      "Equipment Datasheets",
      "Hydraulic Calculations",
      "Piping Layouts",
      "General Arrangements",
      "Instrumentation Engineering",
      "Electrical Engineering",
      "Control System Integration",
    ],
  },
  {
    key: "procurement",
    title: "Procurement & Sourcing",
    icon: PackageSearch,
    image: IMG.roMembranes,
    items: [
      "International Sourcing",
      "Vendor Identification",
      "Supplier Qualification Support",
      "Procurement Coordination",
      "Inspection Coordination",
      "Expediting Support",
      "Logistics Coordination",
      "Commercial & Technical Alignment between Buyer and Supplier",
    ],
  },
  {
    key: "consulting",
    title: "Project Management & Consulting",
    icon: Lightbulb,
    image: IMG.controlRoom,
    items: [
      "Concept Development",
      "Feasibility Support",
      "Technology Selection",
      "Budgetary Estimation",
    ],
  },
  {
    key: "tbe",
    title: "Bidding Support Service",
    icon: FileSearch,
    image: IMG.powerPlant,
    items: [
      "Independent Technical Evaluation",
      "Commercial Evaluation of Vendor Offers",
      "Vendor Document Review",
      "Clarifications & Vendor Coordination",
    ],
  },
  {
    key: "commissioning",
    title: "Inspection & Commissioning Support",
    icon: ClipboardCheck,
    image: IMG.controlRoom,
    items: [
      "FAT Support",
      "SAT Support",
      "Pre-Commissioning Assistance",
      "Commissioning Assistance",
      "Inspection Coordination",
    ],
  },
  {
    key: "mechanical",
    title: "Mechanical Equipment Supply Support",
    icon: Cog,
    image: IMG.roMembranes,
    items: [
      "Pumps & Motors",
      "Manual Valves (Ball, Gate, Globe, Check)",
      "Mechanical Seals",
      "Blowers",
      "Tanks & Vessels",
      "Piping Systems (CS, SS, UPVC, HDPE, GRP)",
      "Steel Plates · Dish Heads · Fittings · Bolts & Gaskets",
    ],
  },
  {
    key: "electrical",
    title: "Electrical, Instrumentation & Control",
    icon: Cpu,
    image: IMG.controlRoom,
    items: [
      "MCC & LV Panels",
      "PLC-Based Control Systems",
      "SCADA Systems",
      "Instrumentation (Flow / Pressure / Level Transmitters)",
      "Control & On/Off Valves",
      "Power Transformers & Diesel Generators",
      "Industrial UPS, Batteries & Chargers",
      "LED Lighting, Earthing & Cable Trays",
    ],
  },
];

export const VALUES = [
  { title: "Integrity", icon: ShieldCheck, desc: "Transparent commercial and technical conduct with every client and supplier." },
  { title: "Technical Competence", icon: Settings, desc: "Engineering and procurement work delivered to recognised international standards." },
  { title: "Reliability", icon: Wrench, desc: "Predictable delivery on schedule, scope and budget." },
  { title: "Commitment", icon: ClipboardCheck, desc: "Long-term partnership across the full project lifecycle." },
  { title: "Confidentiality", icon: ShieldCheck, desc: "Project data and commercial information handled with discretion and care." },
  { title: "Client Focus", icon: Users, desc: "Engineering responses tailored to project objectives and operational context." },
];

// Kept for backward compatibility (currently unused by pages — Projects page now shows Representative Experience block)
export const PROJECT_EXPERIENCE = [];
